<template>
  <!-- The QR gun guide (docs/tutorial-pistola-qr.md): a full screen, the stage on the left (a drawing, the
       Bluetooth screen drawn in CSS, a real photo or the live test) and one step's words on the right. Twin of
       the periscope guide. -->
  <section
    class="qg"
    data-testid="qr-gun-tutorial"
    :data-step="step"
    :aria-labelledby="titleId"
  >
    <header class="qg-bar">
      <BzButton
        class="qg-exit"
        kind="secondary"
        size="md"
        icon="close"
        :label="$t('message.qrGunTutorialExit')"
        data-testid="qr-gun-exit"
        @click="exit"
      />
      <span class="qg-bar-title">{{ $t('message.qrGunTutorialTitle') }}</span>
      <div v-if="stepNumber" class="qg-progress" data-testid="qr-gun-progress">
        <span class="qg-progress-text">{{ $t('message.qrGunTutorialStepOf', { step: stepNumber, total: totalSteps }) }}</span>
        <span class="qg-dots" aria-hidden="true">
          <i v-for="n in totalSteps" :key="n" class="qg-dot" :class="{ 'is-done': n <= stepNumber }"></i>
        </span>
      </div>
    </header>

    <div class="qg-main">
      <div ref="stage" class="qg-stage">
        <transition name="qg-fade" mode="out-in">
          <!-- POWER, NOTFOUND and SLEEP: an illustrated scene on a 760x640 stage that covers the box. -->
          <div v-if="isArt" :key="step" class="qg-holder">
            <!-- Re-keying this element is what "Ver otra vez" does: a new element starts its animations again. -->
            <div :key="replayKey" class="qg-scene" :style="sceneStyle">
              <!-- The gun on the pass (src/qrGunTutorialArt.js). Its finished pose is the gun awake: trigger
                   released, LED lit, the beep; the motion (power: off → press → on; sleep: on → off → press →
                   a second → on) is CSS, per scene. The two trigger frames are swapped, never moved: the pressed one
                   is cut where it goes into the grip. -->
              <div class="qg-art" :data-scene="step">
                <img v-for="k in BASE_LAYERS" :key="k" class="qg-layer" :src="layers[k].src" :style="box(k)" alt="" aria-hidden="true" draggable="false" />
                <img class="qg-layer qg-trig0" :src="layers.gunTrigger0.src" :style="box('gunTrigger0')" alt="" aria-hidden="true" draggable="false" />
                <img class="qg-layer qg-trig1" :src="layers.gunTrigger1.src" :style="box('gunTrigger1')" alt="" aria-hidden="true" draggable="false" />
                <img class="qg-layer" :src="layers.gunHead.src" :style="box('gunHead')" alt="" aria-hidden="true" draggable="false" />
                <img class="qg-layer qg-led" :src="layers.gunLed.src" :style="box('gunLed')" alt="" aria-hidden="true" draggable="false" />
                <img class="qg-layer qg-waves" :src="layers.gunWaves.src" :style="[box('gunWaves'), wavesOrigin]" alt="" aria-hidden="true" draggable="false" />
                <span class="qg-ripple" :style="pressAt" aria-hidden="true"></span>
                <span class="qg-tap" :style="pressAt" aria-hidden="true">
                  <BzIcon name="touch_app" class="qg-tap-icon" />
                </span>
              </div>
            </div>
          </div>

          <!-- PAIR: the Bluetooth settings of the tablet drawn in CSS (Android's light look, from the HONOR's):
               the gun's row is under the available devices, gets tapped and moves to the paired ones, «Conectado».
               The finished pose is the connected one, the row ringed. Only the gun's own row is shown. -->
          <div v-else-if="step === 'pair'" key="pair" class="qg-fit">
            <div class="qg-bt" data-testid="qr-gun-bt-mock" :style="mockStyle" aria-hidden="true">
              <div class="qg-bt-head">
                <span class="qg-bt-title">{{ $t('message.qrGunTutorialBtTitle') }}</span>
                <BzIcon name="help" class="qg-bt-help" />
              </div>
              <div class="qg-bt-card qg-bt-switch">
                <span class="qg-bt-label">{{ $t('message.qrGunTutorialBtTitle') }}</span>
                <span class="qg-bt-toggle"><i class="qg-bt-thumb"></i></span>
              </div>
              <p class="qg-bt-caption">{{ $t('message.qrGunTutorialBtVisible') }}</p>

              <p class="qg-bt-section">{{ $t('message.qrGunTutorialBtPaired') }}</p>
              <div class="qg-bt-slot">
                <div class="qg-bt-card qg-bt-device qg-bt-paired" data-testid="qr-gun-bt-paired">
                  <span class="qg-bt-kb"><i v-for="n in 11" :key="n" :class="{ 'is-space': n === 11 }"></i></span>
                  <span class="qg-bt-text">
                    <span class="qg-bt-name is-linked">{{ GUN_NAME }}</span>
                    <span class="qg-bt-status">{{ $t('message.qrGunTutorialBtConnected') }}</span>
                  </span>
                  <BzIcon name="info" class="qg-bt-info" />
                  <span class="qg-ring"></span>
                </div>
              </div>

              <p class="qg-bt-section">{{ $t('message.qrGunTutorialBtAvailable') }}</p>
              <div class="qg-bt-slot">
                <div class="qg-bt-card qg-bt-device qg-bt-found" data-testid="qr-gun-bt-found">
                  <span class="qg-bt-kb"><i v-for="n in 11" :key="n" :class="{ 'is-space': n === 11 }"></i></span>
                  <span class="qg-bt-text">
                    <span class="qg-bt-name">{{ GUN_NAME }}</span>
                  </span>
                  <span class="qg-ring"></span>
                </div>
                <span class="qg-ripple"></span>
                <span class="qg-tap">
                  <BzIcon name="touch_app" class="qg-tap-icon" />
                </span>
              </div>
            </div>
          </div>

          <!-- BACK: nothing to show of the keyboard icon, so a plain large one. -->
          <div v-else-if="step === 'back'" key="back" class="qg-fit">
            <span class="qg-bigicon" aria-hidden="true">
              <BzIcon name="arrow_back" class="qg-bigicon-svg" />
            </span>
          </div>

          <!-- SCAN: the real bag with its ticket. -->
          <div v-else-if="step === 'scan'" key="scan" class="qg-fit">
            <img class="qg-bag" :src="bagPhoto" :style="fitStyle(BAG_RATIO)" alt="" aria-hidden="true" draggable="false" />
          </div>

          <!-- NOTIFY: the rider toast of a scan, as the kitchen sees it (App.vue paints the real one), and its sound. -->
          <div v-else-if="step === 'notify'" key="notify" class="qg-notify">
            <div class="qg-toast bz-toast" data-testid="qr-gun-toast">
              <span class="bz-toast__icon"><BzIcon name="delivery_dining" /></span>
              <span class="bz-toast__body">
                <span class="bz-chip bz-chip--ok qg-toast-tag">{{ $t('message.qrScanDeliveredTitle') }}</span>
                <span class="bz-toast__code" translate="no">{{ SAMPLE_TICKET }}</span>
              </span>
            </div>
            <BzButton
              kind="secondary"
              size="lg"
              icon="notifications"
              :label="$t('message.qrGunTutorialNotifyListen')"
              data-testid="qr-gun-listen"
              @click="$emit('play-closed-sound')"
            />
          </div>

          <!-- TEST: the code to scan, and what the gun's read says. -->
          <div v-else-if="step === 'test'" key="test" class="qg-test">
            <img class="qg-qr" :src="testQr" alt="" aria-hidden="true" draggable="false" data-testid="qr-gun-test-code" />
            <p
              class="qg-status"
              :class="`is-${testState}`"
              data-testid="qr-gun-test-status"
              :data-state="testState"
              role="status"
              aria-live="polite"
            >
              <BzIcon v-if="testState === 'waiting'" name="progress_activity" class="qg-status-icon bz-spinner" />
              <BzIcon v-else-if="testState === 'success'" name="check_circle" class="qg-status-icon" />
              <BzIcon v-else name="warning" class="qg-status-icon" />
              {{ $t(TEST_STATUS_COPY[testState]) }}
            </p>
          </div>

          <!-- DONE -->
          <div v-else key="done" class="qg-done">
            <span class="qg-ok-badge" aria-hidden="true">
              <BzIcon name="check" class="qg-ok-icon" />
            </span>
          </div>
        </transition>
      </div>

      <div class="qg-copy">
        <transition name="qg-step" mode="out-in">
          <div :key="step" class="qg-copy-inner">
            <h1 :id="titleId" class="qg-title">{{ $t(copy.title) }}</h1>
            <p v-for="key in copy.body" :key="key" class="qg-text">{{ $t(key) }}</p>
            <p v-if="copy.strong" class="qg-text qg-strong">{{ $t(copy.strong) }}</p>
            <p v-if="copy.note" class="qg-note">{{ $t(copy.note) }}</p>
            <div class="qg-actions">
              <BzButton
                v-if="copy.primary"
                kind="primary"
                size="xl"
                block
                :label="$t(copy.primary)"
                data-testid="qr-gun-primary"
                @click="onPrimary"
              />
              <BzButton
                v-if="copy.secondary"
                kind="secondary"
                size="lg"
                block
                :label="$t(copy.secondary)"
                :data-testid="step === 'test' ? 'qr-gun-skip-test' : 'qr-gun-secondary'"
                @click="onSecondary"
              />
              <div v-if="canGoBack || canReplay" class="qg-aux">
                <BzButton
                  v-if="canGoBack"
                  kind="text"
                  size="md"
                  :label="$t('message.qrGunTutorialBack')"
                  data-testid="qr-gun-back"
                  @click="back"
                />
                <BzButton
                  v-if="canReplay"
                  kind="text"
                  size="md"
                  icon="refresh"
                  :label="$t('message.qrGunTutorialReplay')"
                  data-testid="qr-gun-replay"
                  @click="replay"
                />
              </div>
            </div>
          </div>
        </transition>
      </div>
    </div>
  </section>
</template>

<script>
/**
 * QrGunTutorialScreen — the step-by-step guide to the Bluetooth QR gun (docs/tutorial-pistola-qr.md).
 *
 * App.vue owns every piece of state: this screen takes where it was opened from (`qrGunSource`), the step to
 * start at (`qrGunInitialStep`: 'back' when Android killed the app in the Bluetooth settings) and the last
 * scan the gun read (`qrGunScanEvent`, set by App while this screen is open, which closes no order meanwhile). It says
 * two things upward: `qr-gun-close`, with how it ended, and `play-closed-sound` (the arpeggio the app
 * plays for a closed order, for «Escuchar el sonido» and for the test's success). What it remembers is only
 * where the person is in the guide.
 *
 * The three drawn scenes (power, notfound, sleep) are the layers of src/qrGunTutorialArt.js on a 760x640
 * stage scaled by JS to cover the box; their motion is CSS (transform and opacity only), three runs and then
 * the finished pose, and none under reduced motion. The pair step is the same kind of scene made of HTML: the
 * tablet's Bluetooth settings (a light Android screen), the gun's row tapped and moving to the paired devices.
 */
import BzButton from '@/ui/components/BzButton.vue';
import BzIcon from '@/ui/components/BzIcon.vue';
import {
  QR_GUN_TUTORIAL_STEPS, QR_GUN_TUTORIAL_NOTFOUND, QR_GUN_TUTORIAL_DONE, QR_GUN_OUTCOME,
  syncQrGunResume, trackQrGun,
} from '@/qrGunTutorial';
import { STAGE, LAYERS, MOTION } from '@/qrGunTutorialArt';
import bagPhoto from '@/assets/qr-gun-tutorial/bag-ticket.webp';
import testQr from '@/assets/qr-gun-tutorial/test-qr.svg';

const TOTAL_STEPS = QR_GUN_TUTORIAL_STEPS.length;
const STEPS = [...QR_GUN_TUTORIAL_STEPS, QR_GUN_TUTORIAL_DONE];

/** The words of each step: i18n keys only. `body` is a list of paragraphs; `strong` is the sentence to keep. */
const NEXT = 'message.qrGunTutorialNext';
const COPY = {
  power: {
    title: 'message.qrGunTutorialPowerTitle',
    body: ['message.qrGunTutorialPowerBody'],
    strong: 'message.qrGunTutorialPowerStrong',
    primary: NEXT,
  },
  pair: {
    title: 'message.qrGunTutorialPairTitle',
    body: ['message.qrGunTutorialPairBody'],
    note: 'message.qrGunTutorialPairNote',
    primary: NEXT,
    secondary: 'message.qrGunTutorialPairNotFound',
  },
  notfound: {
    title: 'message.qrGunTutorialNotfoundTitle',
    body: ['message.qrGunTutorialNotfoundBody'],
    note: 'message.qrGunTutorialNotfoundNote',
    primary: 'message.qrGunTutorialNotfoundRetry',
  },
  back: {
    title: 'message.qrGunTutorialBackTitle',
    body: ['message.qrGunTutorialBackBody'],
    note: 'message.qrGunTutorialBackNote',
    primary: NEXT,
  },
  // The test has no button to go on when the gun reads: it goes on by itself (or «Continuar igualmente» /
  // Atrás / Salir leave it).
  test: {
    title: 'message.qrGunTutorialTestTitle',
    body: ['message.qrGunTutorialTestBody'],
    note: 'message.qrGunTutorialTestNote',
    secondary: 'message.qrGunTutorialTestSkip',
  },
  scan: {
    title: 'message.qrGunTutorialScanTitle',
    body: ['message.qrGunTutorialScanBody'],
    strong: 'message.qrGunTutorialScanStrong',
    primary: NEXT,
  },
  notify: {
    title: 'message.qrGunTutorialNotifyTitle',
    body: ['message.qrGunTutorialNotifyBody'],
    note: 'message.qrGunTutorialNotifyNote',
    primary: NEXT,
  },
  sleep: {
    title: 'message.qrGunTutorialSleepTitle',
    body: ['message.qrGunTutorialSleepBody'],
    strong: 'message.qrGunTutorialSleepStrong',
    primary: NEXT,
  },
  done: {
    title: 'message.qrGunTutorialDoneTitle',
    body: ['message.qrGunTutorialDoneBody'],
    primary: 'message.qrGunTutorialDoneFinish',
  },
};

/** What the status line of the test says in each state. */
const TEST_STATUS_COPY = Object.freeze({
  waiting: 'message.qrGunTutorialTestWaiting',
  success: 'message.qrGunTutorialTestSuccess',
  other: 'message.qrGunTutorialTestOther',
  partial: 'message.qrGunTutorialTestPartial',
});

/** The branches off the numbered steps, each with the step it hangs off (where Atrás returns to). */
const BRANCH_PARENT = Object.freeze({ [QR_GUN_TUTORIAL_NOTFOUND]: 'pair' });

/** The steps drawn as an isometric scene, and the ones whose animation can be watched again. */
const ART_STEPS = ['power', QR_GUN_TUTORIAL_NOTFOUND, 'sleep'];
const REPLAYABLE = ART_STEPS;
/** The layers under the trigger, the same in every drawn scene. */
const BASE_LAYERS = ['gunPass', 'gunShadow', 'gunBody'];

/** Width / height of the real picture, to fit it whole in the stage. */
const BAG_RATIO = 594 / 788;
/** The air around a picture in the stage, in px. */
const FIT_PAD = 24;
/** The Bluetooth mock-up's own size in px: it is drawn at this size and scaled to fit the stage (never past MOCK_MAX_SCALE). */
const MOCK = Object.freeze({ width: 560, height: 392 });
const MOCK_MAX_SCALE = 1.2;
/** The gun's name in the tablet's Bluetooth list: a product identifier (the suffix changes per unit), not copy. */
const GUN_NAME = 'Hyper 160S-8D6F-UNI';
/** The ticket number on the bag of the photo, which the toast of the notify step names. */
const SAMPLE_TICKET = '968';

/** After a good read, how long «¡Funciona!» is on screen before the guide goes on. */
const SUCCESS_HOLD_MS = 1200;

const px = (n) => `${n}px`;

export default {
  name: 'QrGunTutorialScreen',
  components: { BzButton, BzIcon },
  inheritAttrs: false,
  props: {
    // The `qrGun` prefix: App.vue hands the same bindings to every screen it shows, so a bare `source` or
    // `scanEvent` would land as an attribute on all the others.
    /** Where it was opened from: 'settings' (from the start) or 'resume' (after Android closed the app). */
    qrGunSource: { type: String, default: 'settings' },
    /** The step to start at (one of QR_GUN_TUTORIAL_STEPS). */
    qrGunInitialStep: { type: String, default: 'power' },
    /** The last read of the gun while this screen is open: `seq` counts them, `kind` is what it was. */
    qrGunScanEvent: { type: Object, default: () => ({ seq: 0, kind: null }) },
  },
  emits: ['qr-gun-close', 'play-closed-sound'],
  data() {
    return {
      stepIndex: Math.max(0, STEPS.indexOf(this.qrGunInitialStep)),
      // The branch the person is on, if any ('notfound'): `stepIndex` stays on the step it hangs off.
      branch: '',
      replayKey: 0,
      // The test: 'waiting' | 'success' | 'other' | 'partial'.
      testState: 'waiting',
      // The stage's box, measured: what the scene is scaled by and the pictures fitted to.
      stageW: 0,
      stageH: 0,
      sceneTransform: '',
      titleId: 'qr-gun-tutorial-title',
      // Exposed to the template.
      layers: LAYERS,
      BASE_LAYERS,
      BAG_RATIO,
      GUN_NAME,
      SAMPLE_TICKET,
      TEST_STATUS_COPY,
      bagPhoto,
      testQr,
    };
  },
  computed: {
    step() {
      return this.branch || STEPS[this.stepIndex];
    },
    copy() {
      return COPY[this.step];
    },
    totalSteps() {
      return TOTAL_STEPS;
    },
    /** "Paso n": 1 to 7 on the numbered steps, 0 on the end and the branch. */
    stepNumber() {
      return QR_GUN_TUTORIAL_STEPS.indexOf(this.step) + 1;
    },
    isArt() {
      return ART_STEPS.includes(this.step);
    },
    canGoBack() {
      return this.branch !== '' || (this.stepIndex > 0 && this.step !== QR_GUN_TUTORIAL_DONE);
    },
    canReplay() {
      return REPLAYABLE.includes(this.step);
    },
    /** The Bluetooth mock-up, centred in the stage and scaled to fit it whole (before the stage is measured, as drawn). */
    mockStyle() {
      const scale = this.stageW && this.stageH
        ? Math.min((this.stageW - 2 * FIT_PAD) / MOCK.width, (this.stageH - 2 * FIT_PAD) / MOCK.height, MOCK_MAX_SCALE)
        : 1;
      return {
        width: px(MOCK.width),
        height: px(MOCK.height),
        marginLeft: px(-MOCK.width / 2),
        marginTop: px(-MOCK.height / 2),
        transform: `scale(${Math.max(scale, 0.1)})`,
      };
    },
    sceneStyle() {
      return { width: px(STAGE.width), height: px(STAGE.height), transform: this.sceneTransform };
    },
    /** The tap badge and its ripple, on the trigger. */
    pressAt() {
      return { left: px(MOTION.press[0]), top: px(MOTION.press[1]) };
    },
    /** The beep's arcs grow from where they start. */
    wavesOrigin() {
      return { transformOrigin: `${px(MOTION.waves[0])} ${px(MOTION.waves[1])}` };
    },
  },
  watch: {
    'qrGunScanEvent.seq'() {
      this.onScan(this.qrGunScanEvent && this.qrGunScanEvent.kind);
    },
  },
  created() {
    // Not reactive on purpose: the pending hand-over to the last step.
    this._advanceTimer = null;
  },
  mounted() {
    this.measureStage();
    if (typeof ResizeObserver !== 'undefined') {
      this._observer = new ResizeObserver(() => this.measureStage());
      this._observer.observe(this.$refs.stage);
    } else {
      window.addEventListener('resize', this.measureStage);
    }
    this.trackStep();
    this.syncResume();
  },
  beforeUnmount() {
    clearTimeout(this._advanceTimer);
    if (this._observer) this._observer.disconnect();
    window.removeEventListener('resize', this.measureStage);
  },
  methods: {
    track(event, props = {}) {
      trackQrGun(event, { ...props, source: this.qrGunSource });
    },
    /** `index` is the step's "Paso n" (1-7), 8 for the end and, on a branch, the index of the step it hangs off. */
    trackStep() {
      const n = this.stepNumber;
      let index = n || TOTAL_STEPS + 1;
      if (BRANCH_PARENT[this.step]) index = QR_GUN_TUTORIAL_STEPS.indexOf(BRANCH_PARENT[this.step]) + 1;
      this.track('step', { step: this.step, index });
    },
    /** Where the person may leave the app (looking for the gun, coming back) the resume mark is kept. */
    syncResume() {
      try {
        syncQrGunResume(window.localStorage, this.step);
      } catch (_) { /* storage may be denied */ }
    },

    // ---- the stage ----
    /** A layer's box on the 760x640 stage. */
    box(key) {
      const { x, y, w, h } = LAYERS[key];
      return { left: px(x), top: px(y), width: px(w), height: px(h) };
    },
    measureStage() {
      const el = this.$refs.stage;
      if (!el) return;
      const w = el.clientWidth;
      const h = el.clientHeight;
      if (!w || !h) return;
      this.stageW = w;
      this.stageH = h;
      // Cover, centred: the scenes keep what matters at the middle, so what a narrow box crops is air.
      const s = Math.max(w / STAGE.width, h / STAGE.height);
      this.sceneTransform = `translate(${(w - STAGE.width * s) / 2}px, ${(h - STAGE.height * s) / 2}px) scale(${s})`;
    },
    /** The size at which a picture of this width/height ratio fits whole in the stage, with its air. */
    fitStyle(ratio) {
      if (!this.stageW || !this.stageH) return {};
      const width = Math.max(0, Math.min(this.stageW - 2 * FIT_PAD, (this.stageH - 2 * FIT_PAD) * ratio));
      return { width: px(width), height: px(width / ratio) };
    },

    // ---- the flow ----
    goTo(index) {
      clearTimeout(this._advanceTimer);
      this.stepIndex = index;
      this.branch = '';
      this.testState = 'waiting';
      this.replayKey += 1;
      this.trackStep();
      this.syncResume();
    },
    goToBranch(id) {
      clearTimeout(this._advanceTimer);
      this.branch = id;
      this.replayKey += 1;
      this.trackStep();
      this.syncResume();
    },
    next() {
      if (this.stepIndex < STEPS.length - 1) this.goTo(this.stepIndex + 1);
    },
    back() {
      if (!this.canGoBack) return;
      // From a branch, back is the step it hangs off (stepIndex never moved).
      this.goTo(this.branch ? this.stepIndex : this.stepIndex - 1);
    },
    replay() {
      this.replayKey += 1;
    },
    onPrimary() {
      if (this.step === QR_GUN_TUTORIAL_DONE) {
        this.$emit('qr-gun-close', { outcome: QR_GUN_OUTCOME.COMPLETED, step: QR_GUN_TUTORIAL_DONE });
      } else if (this.step === QR_GUN_TUTORIAL_NOTFOUND) {
        this.goTo(this.stepIndex); // look again: back to pair
      } else {
        this.next();
      }
    },
    onSecondary() {
      if (this.step === 'pair') {
        this.goToBranch(QR_GUN_TUTORIAL_NOTFOUND);
      } else if (this.step === 'test') {
        // «Continuar igualmente»: on without a read (the gun may be fine and the screen too dim to read).
        this.track('test_skipped');
        this.next();
      }
    },
    exit() {
      clearTimeout(this._advanceTimer);
      this.$emit('qr-gun-close', { outcome: QR_GUN_OUTCOME.DISMISSED, step: this.step });
    },

    // ---- the test ----
    /** The gun read something (classifyTutorialScan's kind). Only the test step listens; the rest of the steps ignore it. */
    onScan(kind) {
      if (this.step !== 'test' || !kind || this.testState === 'success') return;
      this.track('test_scan', { kind });
      if (kind === 'test_code' || kind === 'ticket') {
        this.testState = 'success';
        this.$emit('play-closed-sound');
        clearTimeout(this._advanceTimer);
        this._advanceTimer = setTimeout(() => this.next(), SUCCESS_HOLD_MS);
      } else {
        this.testState = kind === 'partial' ? 'partial' : 'other';
      }
    },
  },
};
</script>

<style scoped>
/* On the design system: only its tokens, the buttons of src/ui/components/ and its spinner. A full screen
   like BzScreen (bar, then content) whose content is two columns: a stage and the words of one step. The
   shell is the periscope guide's (PeriscopeTutorialScreen.vue), under its own prefix. */
.qg {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  background: var(--bz-bg);
  color: var(--bz-ink);
  font-family: var(--bz-font);
  font-synthesis: none;
  font-kerning: normal;
  isolation: isolate;
}

/* ---- the bar: leave, the name, how far along ---- */
.qg-bar {
  flex: none;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: var(--bz-space-4);
  box-sizing: border-box;
  min-height: var(--bz-bar-height);
  padding: env(safe-area-inset-top) max(var(--bz-space-6), env(safe-area-inset-right)) 0 max(var(--bz-space-6), env(safe-area-inset-left));
  border-bottom: 1px solid var(--bz-edge);
}

.qg-exit {
  justify-self: start;
}

.qg-bar-title {
  font-size: var(--bz-text-lg);
  font-weight: var(--bz-weight-label);
  line-height: var(--bz-leading-lg);
  letter-spacing: var(--bz-tracking-lg);
  text-align: center;
}

.qg-progress {
  grid-column: 3;
  justify-self: end;
  display: flex;
  align-items: center;
  gap: var(--bz-space-3);
  color: var(--bz-ink-2);
  font-size: var(--bz-text-md);
  line-height: var(--bz-leading-md);
  white-space: nowrap;
}

.qg-dots {
  display: flex;
  gap: var(--bz-space-2);
}

.qg-dot {
  width: 10px;
  height: 10px;
  border-radius: var(--bz-radius-full);
  background: var(--bz-control);
}

.qg-dot.is-done {
  background: var(--bz-action-ink);
}

/* ---- the content: stage and copy ---- */
.qg-main {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(0, 58fr) minmax(0, 42fr);
  gap: var(--bz-space-7);
  box-sizing: border-box;
  padding: var(--bz-space-6) max(var(--bz-space-6), env(safe-area-inset-right)) max(var(--bz-space-6), env(safe-area-inset-bottom)) max(var(--bz-space-6), env(safe-area-inset-left));
}

.qg-stage {
  position: relative;
  min-height: 0;
  overflow: hidden;
  border-radius: var(--bz-radius-xl);
  background: var(--bz-surface);
}

.qg-copy {
  min-height: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}

/* `margin-block: auto` centres the copy while it fits and lets it scroll from its top when it does not. */
.qg-copy-inner {
  margin-block: auto;
  display: flex;
  flex-direction: column;
  gap: var(--bz-space-4);
}

.qg-title {
  margin: 0;
  font-size: var(--bz-text-3xl);
  font-weight: var(--bz-weight-label);
  line-height: var(--bz-leading-3xl);
  letter-spacing: var(--bz-tracking-3xl);
  color: var(--bz-ink);
  text-wrap: balance;
}

.qg-text {
  margin: 0;
  font-size: var(--bz-text-xl);
  font-weight: var(--bz-weight-body);
  line-height: var(--bz-leading-xl);
  letter-spacing: var(--bz-tracking-xl);
  color: var(--bz-ink-2);
  text-wrap: pretty;
}

/* The sentence to keep, apart from the rest and in the first ink. */
.qg-text.qg-strong {
  font-weight: var(--bz-weight-label);
  color: var(--bz-ink);
}

.qg-note {
  margin: 0;
  font-size: var(--bz-text-md);
  line-height: var(--bz-leading-md);
  color: var(--bz-ink-2);
}

.qg-actions {
  display: flex;
  flex-direction: column;
  gap: var(--bz-target-gap);
  margin-top: var(--bz-space-4);
}

.qg-aux {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: var(--bz-space-2);
}

/* ---- the stage: what every drawn scene stands on ---- */
.qg-holder {
  position: absolute;
  inset: 0;
}

.qg-scene {
  position: absolute;
  left: 0;
  top: 0;
  transform-origin: 0 0;
}

/* The whole stage of the scene (760x640 before scaling), its layers stacked in paint order. */
.qg-art {
  position: absolute;
  inset: 0;
}

.qg-layer {
  position: absolute;
  display: block;
  pointer-events: none;
  user-select: none;
}

/* The finished pose: the gun awake. The pressed trigger is only there while the trigger is down. */
.qg-trig1 {
  opacity: 0;
}

/* The press cue, as the periscope's: a round badge with a hand that comes down on the trigger and presses. */
.qg-tap {
  position: absolute;
  display: grid;
  place-items: center;
  width: 56px;
  height: 56px;
  margin: -28px 0 0 -28px;
  box-sizing: border-box;
  border: 3px solid var(--bz-bg);
  border-radius: var(--bz-radius-full);
  background: var(--bz-action);
  color: var(--bz-on-action);
  opacity: 0;
}

.qg-tap-icon {
  width: var(--bz-icon-lg);
  height: var(--bz-icon-lg);
}

.qg-ripple {
  position: absolute;
  width: 64px;
  height: 64px;
  margin: -32px 0 0 -32px;
  box-sizing: border-box;
  border: 4px solid var(--bz-action-ink);
  border-radius: var(--bz-radius-full);
  opacity: 0;
}

/* POWER and NOTFOUND (4.5 s): off; the badge comes down and presses; the trigger goes in; the LED lights and it
   beeps. SLEEP (6 s): awake; the LED goes out (asleep); the press; about a second of nothing; the LED and the
   beep. Each scene runs three times and stays on its last frame. */
@keyframes qg-power-led {
  0%, 46% { opacity: 0; }
  52%, 100% { opacity: 1; }
}

@keyframes qg-power-waves {
  0%, 50% { opacity: 0; transform: scale(0.6); }
  60%, 100% { opacity: 1; transform: scale(1); }
}

@keyframes qg-power-tap {
  0%, 18% { opacity: 0; transform: translateY(-34px) scale(1); }
  26% { opacity: 1; transform: translateY(-12px) scale(1); }
  34%, 44% { opacity: 1; transform: translateY(0) scale(0.86); }
  52% { opacity: 1; transform: translateY(-10px) scale(1); }
  62%, 100% { opacity: 0; transform: translateY(-10px) scale(1); }
}

@keyframes qg-power-trig0 {
  0%, 33% { opacity: 1; }
  34%, 44% { opacity: 0; }
  45%, 100% { opacity: 1; }
}

@keyframes qg-power-trig1 {
  0%, 33% { opacity: 0; }
  34%, 44% { opacity: 1; }
  45%, 100% { opacity: 0; }
}

@keyframes qg-power-ripple {
  0%, 34% { opacity: 0; transform: scale(0.4); }
  36% { opacity: 1; }
  52%, 100% { opacity: 0; transform: scale(1.6); }
}

@keyframes qg-sleep-led {
  0%, 12% { opacity: 1; }
  24%, 66% { opacity: 0; }
  70%, 100% { opacity: 1; }
}

@keyframes qg-sleep-waves {
  0%, 12% { opacity: 1; transform: scale(1); }
  18%, 68% { opacity: 0; transform: scale(0.6); }
  76%, 100% { opacity: 1; transform: scale(1); }
}

@keyframes qg-sleep-tap {
  0%, 30% { opacity: 0; transform: translateY(-34px) scale(1); }
  36% { opacity: 1; transform: translateY(-12px) scale(1); }
  42%, 50% { opacity: 1; transform: translateY(0) scale(0.86); }
  56% { opacity: 1; transform: translateY(-10px) scale(1); }
  64%, 100% { opacity: 0; transform: translateY(-10px) scale(1); }
}

@keyframes qg-sleep-trig0 {
  0%, 41% { opacity: 1; }
  42%, 50% { opacity: 0; }
  51%, 100% { opacity: 1; }
}

@keyframes qg-sleep-trig1 {
  0%, 41% { opacity: 0; }
  42%, 50% { opacity: 1; }
  51%, 100% { opacity: 0; }
}

@keyframes qg-sleep-ripple {
  0%, 42% { opacity: 0; transform: scale(0.4); }
  44% { opacity: 1; }
  56%, 100% { opacity: 0; transform: scale(1.6); }
}

/* PAIR (6 s): the gun's row is under the available devices; the badge comes down and taps it; the row goes to the
   paired ones. Each run is the same and the last stays on its final frame: the connected one. */
@keyframes qg-bt-found {
  0%, 40% { opacity: 1; }
  46%, 100% { opacity: 0; }
}

@keyframes qg-bt-paired {
  0%, 44% { opacity: 0; transform: translateY(10px); }
  52%, 100% { opacity: 1; transform: translateY(0); }
}

@keyframes qg-bt-tap {
  0%, 10% { opacity: 0; transform: translateY(-34px) scale(1); }
  18% { opacity: 1; transform: translateY(-12px) scale(1); }
  26%, 34% { opacity: 1; transform: translateY(0) scale(0.86); }
  42% { opacity: 1; transform: translateY(-10px) scale(1); }
  50%, 100% { opacity: 0; transform: translateY(-10px) scale(1); }
}

@keyframes qg-bt-ripple {
  0%, 26% { opacity: 0; transform: scale(0.4); }
  28% { opacity: 1; }
  44%, 100% { opacity: 0; transform: scale(1.6); }
}

/* ---- the steps that show a picture, a card or a code: centred in the stage ---- */
.qg-fit,
.qg-notify,
.qg-test,
.qg-done {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--bz-space-5);
  box-sizing: border-box;
  padding: var(--bz-space-5);
}

/* pair: the tablet's Bluetooth settings, drawn. Android's light look (HONOR, 22-oct-2026) on the system's own
   light tokens: the paper is `--bz-ink`, the cards the switch's white (`--bz-handle`), the text the page's dark
   (`--bz-bg`) and its second level `--bz-control`, the blue the action's. 560x392 px at its own size; JS scales it. */
.qg-bt {
  position: absolute;
  left: 50%;
  top: 50%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  padding: var(--bz-space-5) var(--bz-space-6);
  overflow: hidden;
  border-radius: var(--bz-radius-xl);
  background: var(--bz-ink);
  color: var(--bz-bg);
  user-select: none;
  pointer-events: none;
}

.qg-bt-head {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 48px;
  padding: 0 var(--bz-space-2);
}

.qg-bt-title {
  font-size: var(--bz-text-xl);
  font-weight: var(--bz-weight-title);
  line-height: var(--bz-leading-xl);
  letter-spacing: var(--bz-tracking-xl);
}

.qg-bt-help {
  width: var(--bz-icon-lg);
  height: var(--bz-icon-lg);
  color: var(--bz-control);
}

.qg-bt-card {
  position: relative;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  padding: 0 var(--bz-space-4);
  border-radius: var(--bz-radius-lg);
  background: var(--bz-handle);
}

.qg-bt-switch {
  flex: none;
  justify-content: space-between;
  height: 56px;
  margin-top: var(--bz-space-2);
}

.qg-bt-label {
  font-size: var(--bz-text-lg);
  line-height: var(--bz-leading-lg);
  letter-spacing: var(--bz-tracking-lg);
}

/* The switch, on: the action's blue and a white thumb at the right. */
.qg-bt-toggle {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  box-sizing: border-box;
  width: 52px;
  height: 30px;
  padding: 0 var(--bz-space-1);
  border-radius: var(--bz-radius-full);
  background: var(--bz-action);
}

.qg-bt-thumb {
  width: 22px;
  height: 22px;
  border-radius: var(--bz-radius-full);
  background: var(--bz-handle);
}

.qg-bt-caption {
  flex: none;
  margin: var(--bz-space-3) var(--bz-space-3) var(--bz-space-2);
  font-size: var(--bz-text-md);
  line-height: var(--bz-leading-md);
  letter-spacing: var(--bz-tracking-md);
  color: var(--bz-control);
}

.qg-bt-section {
  flex: none;
  margin: var(--bz-space-2) var(--bz-space-3) var(--bz-space-1);
  font-size: var(--bz-text-sm);
  font-weight: var(--bz-weight-title);
  line-height: var(--bz-leading-sm);
  letter-spacing: var(--bz-tracking-sm);
  text-transform: uppercase;
  color: var(--bz-control);
}

/* Where a device's row goes: its height is kept while it is not there, so nothing moves when it comes and goes. */
.qg-bt-slot {
  position: relative;
  flex: none;
  height: 64px;
}

.qg-bt-device {
  height: 100%;
  gap: var(--bz-space-4);
}

/* The ring's wash sits under what the row says, so the blue of the name stays blue. */
.qg-bt-device > :not(.qg-ring) {
  position: relative;
  z-index: 1;
}

.qg-bt-text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.qg-bt-name {
  font-size: var(--bz-text-lg);
  line-height: var(--bz-leading-lg);
  letter-spacing: var(--bz-tracking-lg);
}

.qg-bt-name.is-linked {
  color: var(--bz-action);
}

.qg-bt-status {
  font-size: var(--bz-text-sm);
  line-height: var(--bz-leading-sm);
  letter-spacing: var(--bz-tracking-sm);
  color: var(--bz-control);
}

.qg-bt-info {
  flex: none;
  width: var(--bz-icon-lg);
  height: var(--bz-icon-lg);
  color: var(--bz-control);
}

/* A keyboard, small: its frame and three rows of keys (two of five, and the space bar). */
.qg-bt-kb {
  flex: none;
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  grid-template-rows: repeat(3, 1fr);
  gap: 2px;
  box-sizing: border-box;
  width: 34px;
  height: 24px;
  padding: 3px;
  border: 2px solid currentColor;
  border-radius: var(--bz-radius-sm);
  color: var(--bz-bg);
}

.qg-bt-kb i {
  background: currentColor;
}

.qg-bt-kb i.is-space {
  grid-column: 2 / 5;
}

/* The ring on the gun's row, in translucent amber: a ring and a wash of the same colour, just outside the row. */
.qg-ring {
  position: absolute;
  inset: -6px;
  box-sizing: border-box;
  border: 4px solid var(--bz-warn);
  border-radius: var(--bz-radius-lg);
  pointer-events: none;
}

.qg-ring::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: var(--bz-radius-md);
  background: var(--bz-warn);
  opacity: 0.2;
}

/* The finished pose is the connected one: the gun's row paired, and gone from the available ones. */
.qg-bt-found {
  opacity: 0;
}

.qg-bt .qg-tap,
.qg-bt .qg-ripple {
  left: 62%;
  top: 56%;
}

/* back: one large icon on a disc. */
.qg-bigicon {
  display: grid;
  place-items: center;
  width: calc(var(--bz-target-hero) * 2);
  height: calc(var(--bz-target-hero) * 2);
  border-radius: var(--bz-radius-full);
  background: var(--bz-control);
  color: var(--bz-ink);
}

.qg-bigicon-svg {
  width: calc(var(--bz-icon-lg) * 3);
  height: calc(var(--bz-icon-lg) * 3);
}

/* scan: the real photo, whole. */
.qg-bag {
  display: block;
  flex: none;
  object-fit: cover;
  border-radius: var(--bz-radius-lg);
  user-select: none;
  pointer-events: none;
}

/* notify: the toast of a scan, as the app paints it. */
.qg-toast {
  pointer-events: none;
}

.qg-toast .bz-toast__icon {
  background: var(--bz-ok-container);
  color: var(--bz-on-ok-container);
}

.qg-toast-tag {
  align-self: flex-start;
  margin-bottom: var(--bz-space-1);
}

/* test: the code on its own white (the quiet zone is in the picture), big enough to read from a tablet's screen. */
.qg-test {
  overflow-y: auto;
}

.qg-qr {
  display: block;
  flex: none;
  width: min(300px, 100%);
  min-width: 240px;
  aspect-ratio: 1;
  border-radius: var(--bz-radius-md);
  user-select: none;
  pointer-events: none;
}

.qg-status {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--bz-space-3);
  margin: 0;
  max-width: 100%;
  font-size: var(--bz-text-lg);
  font-weight: var(--bz-weight-label);
  line-height: var(--bz-leading-lg);
  letter-spacing: var(--bz-tracking-lg);
  color: var(--bz-ink-2);
  text-align: center;
  text-wrap: balance;
}

.qg-status-icon {
  flex: none;
  width: var(--bz-icon-lg);
  height: var(--bz-icon-lg);
}

.qg-status.is-success {
  color: var(--bz-ink);
}

.qg-status.is-success .qg-status-icon {
  color: var(--bz-ok);
}

.qg-status.is-other .qg-status-icon,
.qg-status.is-partial .qg-status-icon {
  color: var(--bz-warn);
}

/* ---- done: the check ---- */
.qg-ok-badge {
  display: grid;
  place-items: center;
  width: var(--bz-target-hero);
  height: var(--bz-target-hero);
  border-radius: var(--bz-radius-full);
  background: var(--bz-ok);
  color: var(--bz-on-ok-solid);
}

.qg-ok-icon {
  width: calc(var(--bz-icon-lg) * 2);
  height: calc(var(--bz-icon-lg) * 2);
}

/* ---- motion ---- */
/* A step comes in with a short fade and a nudge, as the help flow's does. The drawn scenes bring their own
   motion (three runs, then the finished pose) inside `.qg-art`, all of it under this same media query. */
@media (prefers-reduced-motion: no-preference) {
  .qg-step-enter-active,
  .qg-step-leave-active {
    transition: opacity 80ms ease-out, transform 80ms ease-out;
  }

  .qg-step-enter-from { transform: translateX(10px); }
  .qg-step-leave-to { transform: translateX(-10px); }

  .qg-art[data-scene="power"] .qg-led,
  .qg-art[data-scene="notfound"] .qg-led { animation: qg-power-led 4.5s linear 0s 3 both; }
  .qg-art[data-scene="power"] .qg-waves,
  .qg-art[data-scene="notfound"] .qg-waves { animation: qg-power-waves 4.5s ease-out 0s 3 both; }
  .qg-art[data-scene="power"] .qg-tap,
  .qg-art[data-scene="notfound"] .qg-tap { animation: qg-power-tap 4.5s ease-in-out 0s 3 both; }
  .qg-art[data-scene="power"] .qg-ripple,
  .qg-art[data-scene="notfound"] .qg-ripple { animation: qg-power-ripple 4.5s ease-out 0s 3 both; }
  .qg-art[data-scene="power"] .qg-trig0,
  .qg-art[data-scene="notfound"] .qg-trig0 { animation: qg-power-trig0 4.5s step-end 0s 3 both; }
  .qg-art[data-scene="power"] .qg-trig1,
  .qg-art[data-scene="notfound"] .qg-trig1 { animation: qg-power-trig1 4.5s step-end 0s 3 both; }

  .qg-art[data-scene="sleep"] .qg-led { animation: qg-sleep-led 6s linear 0s 3 both; }
  .qg-art[data-scene="sleep"] .qg-waves { animation: qg-sleep-waves 6s ease-out 0s 3 both; }
  .qg-art[data-scene="sleep"] .qg-tap { animation: qg-sleep-tap 6s ease-in-out 0s 3 both; }
  .qg-art[data-scene="sleep"] .qg-ripple { animation: qg-sleep-ripple 6s ease-out 0s 3 both; }
  .qg-art[data-scene="sleep"] .qg-trig0 { animation: qg-sleep-trig0 6s step-end 0s 3 both; }
  .qg-art[data-scene="sleep"] .qg-trig1 { animation: qg-sleep-trig1 6s step-end 0s 3 both; }

  .qg-bt-found { animation: qg-bt-found 6s ease-in-out 0s 3 both; }
  .qg-bt-paired { animation: qg-bt-paired 6s ease-out 0s 3 both; }
  .qg-bt .qg-tap { animation: qg-bt-tap 6s ease-in-out 0s 3 both; }
  .qg-bt .qg-ripple { animation: qg-bt-ripple 6s ease-out 0s 3 both; }
}

.qg-step-enter-from,
.qg-step-leave-to { opacity: 0; }

.qg-fade-enter-active,
.qg-fade-leave-active { transition: opacity 80ms ease-out; }

.qg-fade-enter-from,
.qg-fade-leave-to { opacity: 0; }

/* ---- responsive ---- */
/* Narrower than 900: the stage on top (up to 45% of the height), the words under it, the column scrolling. */
@media screen and (max-width: 899px) {
  .qg-main {
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: none;
    grid-auto-rows: min-content;
    gap: var(--bz-space-5);
    overflow-y: auto;
    padding-top: var(--bz-space-4);
  }

  .qg-stage {
    height: 45vh;
    min-height: 240px;
  }

  .qg-copy {
    overflow-y: visible;
  }

  .qg-copy-inner {
    margin-block: 0;
  }
}

@media screen and (max-width: 639px) {
  .qg-bar { gap: var(--bz-space-3); padding-inline: max(var(--bz-space-4), env(safe-area-inset-left)) max(var(--bz-space-4), env(safe-area-inset-right)); }
  .qg-bar-title { display: none; }
  .qg-dots { display: none; }
  .qg-title { font-size: var(--bz-text-xl); line-height: var(--bz-leading-xl); letter-spacing: var(--bz-tracking-xl); }
  .qg-text { font-size: var(--bz-text-lg); line-height: var(--bz-leading-lg); letter-spacing: var(--bz-tracking-lg); }
}

/* The tablet over the pass is 614 px tall: the axis that squeezes is the height, so the air between things
   goes and the type and the targets stay. */
@media screen and (max-height: 700px) {
  .qg-main {
    gap: var(--bz-space-5);
    padding-top: var(--bz-space-4);
    padding-bottom: max(var(--bz-space-4), env(safe-area-inset-bottom));
  }

  .qg-copy-inner { gap: var(--bz-space-3); }
  .qg-actions { margin-top: var(--bz-space-2); gap: var(--bz-space-3); }
  .qg-fit,
  .qg-notify,
  .qg-test,
  .qg-done { gap: var(--bz-space-3); padding: var(--bz-space-4); }
}
</style>
